import React, { useCallback, useEffect, useState } from "react";
import TopNav from "@/components/TopNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CalendarDays, MessageCircleMore, TrendingUp, Users } from "lucide-react";
import { api } from "@/lib/api";


export default function FacilitySchedule() {
    const [shifts, setShifts] = useState([]);
    const [capacity, setCapacity] = useState([]);
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        try {
            const [s, c] = await Promise.all([
                api.get("/shifts/facility"),
                api.get("/shifts/capacity?days=14"),
            ]);
            setShifts(s.data);
            setCapacity(c.data.buckets || []);
        } catch (e) { /* noop */ }
    }, []);
    useEffect(() => { load(); }, [load]);

    async function blastSms(id) {
        if (!window.confirm("Blast open-shift SMS to all eligible caregivers?")) return;
        setBusy(id);
        try {
            const r = await api.post(`/shifts/${id}/blast-sms`);
            toast.success(`SMS blast sent to ${r.data.sent} of ${r.data.eligible} caregivers`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Blast failed");
        } finally {
            setBusy(null);
        }
    }

    const byDay = shifts.reduce((acc, s) => {
        const d = (s.start || "").slice(0, 10);
        if (!d) return acc;
        (acc[d] ||= []).push(s);
        return acc;
    }, {});
    const days = Object.keys(byDay).sort();

    const openTotal = shifts.filter((s) => s.status === "open").length;
    const filledTotal = shifts.filter((s) => s.status === "claimed" || s.status === "completed").length;

    return (
        <div className="min-h-screen bg-stone-50">
            <TopNav />
            <main data-testid="facility-page" className="max-w-6xl mx-auto px-6 lg:px-8 py-10 animate-fade-in">
                <div className="mb-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-2 flex items-center gap-1.5">
                        <CalendarDays className="w-3.5 h-3.5" /> B2B facility scheduling
                    </div>
                    <h1 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
                        Team Schedule
                    </h1>
                    <p className="text-stone-600 font-dmsans mt-2 max-w-2xl">
                        Full visibility of every shift across your facility. Plan capacity, and fill open shifts with a single SMS blast to your caregiver pool.
                    </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                    <StatCard icon={<Users className="w-4 h-4" />} label="Total shifts" value={shifts.length} tint="stone" />
                    <StatCard icon={<CalendarDays className="w-4 h-4" />} label="Filled" value={filledTotal} tint="emerald" />
                    <StatCard icon={<TrendingUp className="w-4 h-4" />} label="Open" value={openTotal} tint="amber" />
                    <StatCard icon={<MessageCircleMore className="w-4 h-4" />} label="Coverage · 14d" value={pctCoverage(capacity)} suffix="%" tint="indigo" />
                </div>

                <Tabs defaultValue="calendar" className="w-full">
                    <TabsList data-testid="facility-tabs" className="rounded-full h-11 p-1">
                        <TabsTrigger value="calendar" data-testid="facility-tab-calendar" className="rounded-full px-5 h-9 text-xs font-outfit font-medium">Calendar</TabsTrigger>
                        <TabsTrigger value="capacity" data-testid="facility-tab-capacity" className="rounded-full px-5 h-9 text-xs font-outfit font-medium">Capacity planning</TabsTrigger>
                    </TabsList>

                    <TabsContent value="calendar" className="mt-6">
                        <Card className="rounded-2xl border-stone-200 overflow-hidden">
                            <CardContent className="p-0 divide-y divide-stone-100">
                                {days.length === 0 && (
                                    <div className="p-8 text-center text-sm text-stone-500 font-dmsans">
                                        No shifts scheduled. Create shifts from the Shifts page.
                                    </div>
                                )}
                                {days.map((d) => (
                                    <div key={d} className="p-4">
                                        <div className="font-outfit text-sm font-semibold text-stone-900 mb-2">
                                            {new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                                        </div>
                                        <div className="space-y-2">
                                            {byDay[d].map((s) => (
                                                <div key={s.id} data-testid={`facility-shift-row-${s.id}`} className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3">
                                                    <Badge className={statusBadge(s.status)}>{s.status}</Badge>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-outfit font-semibold text-stone-900 truncate">{s.title}</div>
                                                        <div className="text-xs text-stone-500 font-dmsans truncate">
                                                            {formatRange(s.start, s.end)} · {s.location}
                                                            {s.caregiver_name && <> · <span className="text-emerald-700 font-medium">{s.caregiver_name}</span></>}
                                                        </div>
                                                    </div>
                                                    <div className="text-xs font-dmsans text-stone-600 whitespace-nowrap">
                                                        ${((s.rate_cents || 0) / 100).toFixed(0)}/hr
                                                    </div>
                                                    {s.status === "open" && (
                                                        <Button size="sm" onClick={() => blastSms(s.id)} disabled={busy === s.id} data-testid={`facility-blast-btn-${s.id}`} className="rounded-full bg-indigo-700 hover:bg-indigo-800 text-white h-8 px-3 text-xs whitespace-nowrap">
                                                            <MessageCircleMore className="w-3 h-3 mr-1" /> {busy === s.id ? "Sending..." : "Blast SMS"}
                                                        </Button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="capacity" className="mt-6">
                        <Card className="rounded-2xl border-stone-200 overflow-hidden">
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Total</TableHead>
                                            <TableHead>Filled</TableHead>
                                            <TableHead>Open</TableHead>
                                            <TableHead>Coverage</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {capacity.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5} className="text-center text-sm text-stone-500 py-10 font-dmsans">
                                                    No shifts scheduled in the next 14 days.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {capacity.map((b) => {
                                            const pct = b.total ? Math.round((b.filled / b.total) * 100) : 0;
                                            const color = pct >= 90 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-red-500";
                                            return (
                                                <TableRow key={b.date} data-testid={`facility-capacity-row-${b.date}`}>
                                                    <TableCell>
                                                        <div className="text-sm font-outfit font-medium text-stone-900">
                                                            {new Date(b.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-sm font-dmsans">{b.total}</TableCell>
                                                    <TableCell className="text-sm font-dmsans text-emerald-700 font-medium">{b.filled}</TableCell>
                                                    <TableCell className="text-sm font-dmsans text-amber-700 font-medium">{b.open}</TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-24 h-2 rounded-full bg-stone-200 overflow-hidden">
                                                                <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
                                                            </div>
                                                            <div className="text-xs font-dmsans text-stone-600 w-10">{pct}%</div>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}


function StatCard({ icon, label, value, suffix = "", tint = "stone" }) {
    const tints = {
        stone: "bg-stone-100 text-stone-800",
        emerald: "bg-emerald-100 text-emerald-800",
        amber: "bg-amber-100 text-amber-800",
        indigo: "bg-indigo-100 text-indigo-800",
    };
    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-4">
            <div className={`inline-flex w-7 h-7 items-center justify-center rounded-full ${tints[tint]}`}>{icon}</div>
            <div className="text-2xl font-outfit font-semibold text-stone-900 mt-2">{value}{suffix}</div>
            <div className="text-xs text-stone-500 font-dmsans uppercase tracking-wider mt-1">{label}</div>
        </div>
    );
}


function statusBadge(status) {
    const c = {
        open: "bg-amber-100 text-amber-800",
        claimed: "bg-emerald-100 text-emerald-800",
        completed: "bg-stone-100 text-stone-700",
        paid: "bg-indigo-100 text-indigo-800",
        cancelled: "bg-red-100 text-red-700",
    };
    return `${c[status] || c.open} rounded-full text-[10px] uppercase tracking-wider`;
}


function formatRange(start, end) {
    if (!start || !end) return "";
    const s = new Date(start);
    const e = new Date(end);
    return `${s.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} – ${e.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}


function pctCoverage(buckets) {
    const total = buckets.reduce((a, b) => a + b.total, 0);
    const filled = buckets.reduce((a, b) => a + b.filled, 0);
    return total ? Math.round((filled / total) * 100) : 0;
}
